import { createSlice } from "@reduxjs/toolkit";
import { factory } from "../../api/apiFactory";
import { showNotification } from "./notificationSlice";
import dayjs from "dayjs";

const onlineUsersApi = factory.get("online_users");

const initialState = {
  data: {
    online_users: 0,
    offline_users: 0,
    total_calls: 0,
    customer_count: 0,
    total_failed_calls: 0,
    total_success_calls: 0,
    task_maintenance: 0,
    task_renew: 0,
    task_renew_canceled: 0,
    task_renew_completed: 0,
    customer_renewal_count: 0,
    customer_not_renewal_count: 0,
    users: [],
  },
  filters: {
    skip: 0,
    take: 25,
    page: 0,
    start_date: dayjs().format("YYYY-MM-DD"),
    end_date: dayjs().format("YYYY-MM-DD"),
    search: "",
    status: "all", // "all" | "online" | "offline"
    view_mode: "my", // "my" (احصائياتي) | "all" (جميع الموظفين)
  },
  loading: false,
};

const onlineUsersSlice = createSlice({
  name: "onlineUsers",
  initialState,
  reducers: {
    setOnlineUsersData: (state, { payload }) => {
      const source = payload?.data || payload;
      if (source) {
        state.data = {
          ...state.data,
          ...source,
          online_users: source.online_users ?? 0,
          offline_users: source.offline_users ?? 0,
          total_calls: source.total_calls ?? 0,
          customer_count: source.customer_count ?? 0,
          total_failed_calls: source.total_failed_calls ?? 0,
          total_success_calls: source.total_success_calls ?? 0,
          task_maintenance: source.task_maintenance ?? 0,
          task_renew: source.task_renew ?? 0,
          task_renew_canceled: source.task_renew_canceled ?? 0,
          task_renew_completed: source.task_renew_completed ?? 0,
          customer_renewal_count: source.customer_renewal_count ?? 0,
          customer_not_renewal_count: source.customer_not_renewal_count ?? 0,
          users: Array.isArray(source.users) ? source.users : [],
        };
      }
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setFilters: (state, { payload }) => {
      state.filters = {
        ...state.filters,
        ...payload,
      };
    },
    resetFilters: (state) => {
      state.filters = {
        skip: 0,
        take: 25,
        page: 0,
        start_date: dayjs().format("YYYY-MM-DD"),
        end_date: dayjs().format("YYYY-MM-DD"),
        search: "",
        status: "all",
        view_mode: "my",
      };
    },
  },
});

export const {
  setOnlineUsersData,
  setLoading,
  setFilters,
  resetFilters,
} = onlineUsersSlice.actions;

export default onlineUsersSlice.reducer;

export const getOnlineUsers = (customParams = null) => async (dispatch, getState) => {
  try {
    const isExcel = Boolean(customParams?.is_excel);
    dispatch(setLoading(true));
    const currentFilters = getState().onlineUsers.filters;
    const params = {
      start_date: customParams?.start_date || currentFilters.start_date || dayjs().format("YYYY-MM-DD"),
      end_date: customParams?.end_date || currentFilters.end_date || dayjs().format("YYYY-MM-DD"),
      skip: currentFilters.skip,
      take: currentFilters.take,
    };

    if (isExcel) {
      params.is_excel = 1;
    }

    const permissions = getState().profile?.user?.info?.permissions || [];
    const canSeeAllUsers = permissions.includes("customer_call_statistics");
    const viewMode = customParams?.view_mode || currentFilters.view_mode || "my";

    const shouldFetchAll = canSeeAllUsers && viewMode === "all";

    const res = shouldFetchAll
      ? await onlineUsersApi.listOnlineUsers(params)
      : await onlineUsersApi.listOnlineCurrentUser(params);

    if (isExcel) {
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `online_users_${dayjs().format("YYYY-MM-DD")}.xlsx`;
      a.click();
      dispatch(setLoading(false));
      return res.data;
    }

    dispatch(setOnlineUsersData(res.data));
    dispatch(setLoading(false));
    return res.data;
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(
      showNotification({
        message: err?.response?.data?.message || err?.message || "حدث خطأ أثناء جلب بيانات الموظفين",
        type: "error",
      })
    );
    throw err;
  }
};

export const toExcel = () => async (dispatch) => {
  return dispatch(getOnlineUsers({ is_excel: true }));
};

export const OnlineUsers = {
  getOnlineUsers,
  toExcel,
};
